import { Box, Flex, Heading, SimpleGrid, Text, Badge, Icon } from "@chakra-ui/react";
import { RootState, useAppSelector } from "../../../store";
import { formatDate } from "../../../helpers/formatDate";
import { ViewInformationBlock } from "@/components/ViewInformationBlock/ViewInformationBlock";
import { FiFile } from "react-icons/fi";
import { AttachmentIcon } from "@chakra-ui/icons";
import { downloadFile } from "@/helpers/DownloadFile";

export const SingleVacancyBlock = () => {
  const { currentVacancy } = useAppSelector((state: RootState) => state.vacancy);

  return (
    <Flex gap="16px" alignItems="flex-start">
      <Flex flexDirection="column" gap="16px" flex={1} minW={0}>
        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
          <Flex alignItems="center" gap="8px" mb="16px">
            <Heading as="h3" size="md" fontWeight={600}>
              Информация о проекте
            </Heading>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <InfoItem label="Проект">{currentVacancy?.project?.name ?? "—"}</InfoItem>
            <InfoItem label="Заказчик">{currentVacancy?.customer?.name ?? "—"}</InfoItem>
            <InfoItem label="Юр. лицо">{(currentVacancy as any)?.legal_entity?.name ?? "—"}</InfoItem>
          </SimpleGrid>
          <Flex direction="column" gap="6px" mt="16px">
            <Text fontSize="14px" color="#374151" fontWeight={700}>
              Описание проекта
            </Text>
            <Text color="#4a5568" fontSize="14px" lineHeight="1.5" whiteSpace="pre-line">
              {currentVacancy?.project_description || "—"}
            </Text>
          </Flex>
        </Box>
        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
          <Heading as="h3" size="md" fontWeight={600} mb="12px">
            Требования к кандидату
          </Heading>
          <Text color="#4a5568" fontSize="14px" lineHeight="1.5" whiteSpace="pre-line">
            {currentVacancy?.candidate_requirements || "—"}
          </Text>
        </Box>
        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
          <Heading as="h3" size="md" fontWeight={600} mb="12px">
            Обязанности кандидата
          </Heading>
          <Text color="#4a5568" fontSize="14px" lineHeight="1.5" whiteSpace="pre-line">
            {currentVacancy?.job_duties || "—"}
          </Text>
        </Box>
        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
          <Heading as="h3" size="md" fontWeight={600} mb="12px">
            Финансовая информация
          </Heading>
          <Flex direction="column" gap="16px">
            <Text color="#65758b" fontSize="14px" fontWeight={600}>
              Макс ставки продажи
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <FinanceItem label="Общая" value={(currentVacancy as any)?.sales_max_offer_general ?? (currentVacancy as any)?.sales_max_offer} />
              <FinanceItem label="МСК и МО" value={(currentVacancy as any)?.sales_max_offer_moscow} />
              <FinanceItem label="Регион" value={(currentVacancy as any)?.sales_max_offer_regions} />
            </SimpleGrid>
            <Text color="#65758b" fontSize="14px" fontWeight={600}>
              Закупка и маржинальность
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <FinanceItem label="Ставка закупки (max)" value={(currentVacancy as any)?.purchase_rate_max} />
              <FinanceItem
                label="Маржинальность"
                value={(currentVacancy as any)?.marginality}
                suffix={typeof (currentVacancy as any)?.marginality === "number" ? "%" : undefined}
              />
            </SimpleGrid>
            <Text color="#65758b" fontSize="14px" fontWeight={600}>
              Зарплаты на руки в месяц
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <FinanceItem label="По ТК" value={(currentVacancy as any)?.salary_tk_max} />
              <FinanceItem label="По ИП" value={(currentVacancy as any)?.salary_ip_max} />
              <FinanceItem label="По ГПХ" value={(currentVacancy as any)?.salary_gph_max} />
            </SimpleGrid>
          </Flex>
        </Box>
        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
          <Heading as="h3" size="md" fontWeight={600} mb="12px">
            Условия работы
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <InfoItem label="Формат работы">{(currentVacancy as any)?.job_format?.name ?? "—"}</InfoItem>
            <InfoItem label="Занятость">{(currentVacancy as any)?.job_workload?.name ?? "—"}</InfoItem>
            <InfoItem label="Локация поиска">{(currentVacancy as any)?.location ?? "—"}</InfoItem>
            <InfoItem label="Адрес офиса">{(currentVacancy as any)?.office_address ?? "—"}</InfoItem>
          </SimpleGrid>
        </Box>
        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
          <Heading as="h3" size="md" fontWeight={600} mb="12px">
            Дополнительная информация
          </Heading>
          <SimpleGrid columns={{ base: 1 }} spacing={6}>
            <InfoItem label="Комментарии к вакансии">{(currentVacancy as any)?.comments || "—"}</InfoItem>
          </SimpleGrid>
        </Box>
        <ViewInformationBlock icon={<Icon as={FiFile as any} boxSize={5} color="#3b515e" />} title="Вложения">
          <Flex gap="4px" flexWrap="wrap">
            {(() => {
              const entries = currentVacancy?.attachments ? Object.entries(currentVacancy.attachments) : [];
              if (entries.length === 0) {
                return (
                  <Text fontSize="16px" color="#111827" wordBreak="break-word">
                    —
                  </Text>
                );
              }
              return entries.map(([_key, el]) => (
                <Flex
                  w="100%"
                  key={el.id}
                  display="flex"
                  alignItems="center"
                  gap="6px"
                  bgColor="#f1f5f9"
                  p="4px 8px"
                  borderRadius="4px"
                  sx={{
                    "&:hover": {
                      cursor: "pointer",
                      opacity: 0.6,
                    },
                  }}
                  onClick={() => downloadFile(el.id, el.name)}
                >
                  <AttachmentIcon style={{ verticalAlign: "text-top" }} />
                  <Text display="inline">{el.name}</Text>
                </Flex>
              ));
            })()}
          </Flex>
        </ViewInformationBlock>
      </Flex>
      <Flex flexDirection="column" gap="16px" minW={0} w={{ base: "100%", md: "360px" }}>
        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
          <Heading as="h3" size="md" fontWeight={600} mb="12px">
            Основная информация
          </Heading>
          <Flex flexDirection="column" gap="12px">
            <InfoItem label="Название должности">{currentVacancy?.name ?? "—"}</InfoItem>
            <InfoItem label="Профиль">{(currentVacancy as any)?.profile?.name ?? "—"}</InfoItem>
            <InfoItem label="Грейд">
              {Array.isArray(currentVacancy?.candidate_grade) && currentVacancy?.candidate_grade?.length
                ? currentVacancy?.candidate_grade?.map((g: any) => g?.name).join(", ")
                : "—"}
            </InfoItem>
            <InfoItem label="Срок привлечения">{(currentVacancy as any)?.attraction_period?.name ?? "—"}</InfoItem>
            <InfoItem label="Требуется человек">{currentVacancy?.required_number ?? "—"}</InfoItem>
            <Flex flexDirection="column" gap="6px" minW={0}>
              <Text fontSize="14px" color="#374151" fontWeight={700}>
                Статус вакансии
              </Text>
              <Badge variant="subtle" colorScheme="gray" w="fit-content">
                {(currentVacancy as any)?.status?.name ?? "—"}
              </Badge>
            </Flex>
          </Flex>
        </Box>
        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
          <Heading as="h3" size="md" fontWeight={600} mb="12px">
            Информация о создании
          </Heading>
          <Flex flexDirection="column" gap="12px">
            <InfoItem label="Кем создано">
              {(currentVacancy as any)?.created_by?.name ?? (currentVacancy as any)?.created_by?.username ?? "—"}
            </InfoItem>
            <InfoItem label="Дата создания">{currentVacancy?.created_at ? formatDate(currentVacancy.created_at) : "—"}</InfoItem>
          </Flex>
        </Box>
        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
          <Heading as="h3" size="md" fontWeight={600} mb="12px">
            Назначенные рекрутеры
          </Heading>
          <Flex flexDirection="column" gap="8px">
            {Array.isArray(currentVacancy?.recruiters) && (currentVacancy as any)?.recruiters?.length > 0 ? (
              ((currentVacancy as any)?.recruiters || []).map((r: any) => (
                <Text key={r?.id} fontSize="14px" color="#111827">
                  {r?.name ?? r?.username}
                </Text>
              ))
            ) : (
              <Text fontSize="14px" color="#6B7280">
                Рекрутеры не назначены
              </Text>
            )}
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
};

const InfoItem = ({ label, children }: { label: string; children: any }) => {
  const normalizeValue = (value: any): string | number => {
    if (value === null || value === undefined) return "—";
    if (typeof value === "string" && value.trim() === "") return "—";
    return value;
  };
  return (
    <Flex flexDirection="column" gap="6px" minW={0}>
      <Flex alignItems="center" gap="8px">
        <Text fontSize="14px" color="#374151" fontWeight={700}>
          {label}
        </Text>
      </Flex>
      <Text fontSize="16px" color="#111827" wordBreak="break-word" whiteSpace="pre-line">
        {normalizeValue(children)}
      </Text>
    </Flex>
  );
};

const FinanceItem = ({ label, value, suffix }: { label: string; value?: string | number; suffix?: string }) => {
  const display = value === undefined || value === null || value === "" ? "—" : `${value}${suffix ? ` ${suffix}` : ""}`;
  return (
    <Flex flexDirection="row" justifyContent="space-between" minW={0} bgColor="#f3f4f64d" padding="12px" borderRadius="8px">
      <Text fontSize="14px" color="#65758b" fontWeight={500}>
        {label}
      </Text>
      <Text fontSize="16px" color="#3b515e" wordBreak="break-word" fontWeight={700}>
        {display}
      </Text>
    </Flex>
  );
};
